import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { formatDayTime } from '../../lib/format';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { ReportDocument } from './ReportDocument';

/**
 * The saved report (plan §7 `/reports/:id`, §8). Deliberately rendered **outside** the AppShell: a report is
 * a full-width document, and the browser's print dialog has to see the page and nothing else. The slim action
 * bar carries the only two things a reader needs — back, and print — and is hidden from the printout by
 * `.report-print-hidden`.
 */
export const ReportViewPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();

  const report = useQuery(orpc.reports.get.queryOptions({ input: { id }, enabled: id !== '', retry: false }));

  return (
    <div className="report-shell min-h-screen bg-muted/40">
      <div className="report-print-hidden sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-6 py-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="size-4" />
          {t('reports.view.back')}
        </Button>

        <div className="min-w-0 text-center">
          <p className="truncate text-sm font-medium text-card-foreground">{report.data?.title ?? t('reports.view.loading')}</p>
          {report.data ? (
            <p className="truncate text-xs text-muted-foreground">
              {report.data.scenarioCode} · {report.data.createdByName ?? t('common.notSet')} · {formatDayTime(report.data.createdAt)}
            </p>
          ) : null}
        </div>

        <Button
          size="sm"
          onClick={() => window.print()}
          disabled={!report.data}
        >
          <Printer className="size-4" />
          {t('reports.view.print')}
        </Button>
      </div>

      <div className="report-shell px-4 py-6">
        {report.isPending ? (
          <div className="report-print-hidden flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Spinner label={t('app.loading')} />
            {t('app.loading')}
          </div>
        ) : report.isError ? (
          <p
            role="alert"
            className="report-print-hidden mx-auto max-w-xl rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {serverMessage(report.error) ?? t('reports.view.notFound')}
          </p>
        ) : report.data ? (
          <ReportDocument snapshot={report.data.snapshot} />
        ) : null}
      </div>
    </div>
  );
};
